import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { AutomationRule } from '../types';

const FIELDS = [
  'dagrun_pm_automationruleid', 'dagrun_pm_name', 'dagrun_pm_triggertype', 'dagrun_pm_actiontype',
  'dagrun_pm_isenabled', 'dagrun_pm_lasttriggeredon', 'dagrun_pm_triggercount',
  'dagrun_pm_triggerparamsjson', 'dagrun_pm_conditionjson', 'dagrun_pm_actionparamsjson',
  'dagrun_pm_scope', '_dagrun_pm_projectid_value',
];

// Picklists verified via EntityDefinitions on prod.
export const TriggerType = {
  TaskCreated: 100000000, TaskStatusChanged: 100000001, TaskDueDateArrived: 100000002,
  TaskAssigned: 100000003, TaskNoUpdateForNDays: 100000004, IssueCreated: 100000005,
  IssueSeverityChanged: 100000006, RiskProbabilityChanged: 100000007, MilestoneStatusChanged: 100000008,
  CommentAdded: 100000009, TeamMemberAdded: 100000010, CustomFieldChanged: 100000011,
} as const;
export const TRIGGER_LABEL: Record<number, string> = {
  [TriggerType.TaskCreated]: 'Task created',
  [TriggerType.TaskStatusChanged]: 'Task status changed',
  [TriggerType.TaskDueDateArrived]: 'Task due date approaching',
  [TriggerType.TaskAssigned]: 'Task assigned',
  [TriggerType.TaskNoUpdateForNDays]: 'Task not updated for N days',
  [TriggerType.IssueCreated]: 'Issue created',
  [TriggerType.IssueSeverityChanged]: 'Issue severity changed',
  [TriggerType.RiskProbabilityChanged]: 'Risk probability changed',
  [TriggerType.MilestoneStatusChanged]: 'Milestone status changed',
  [TriggerType.CommentAdded]: 'Comment added',
  [TriggerType.TeamMemberAdded]: 'Team member added',
  [TriggerType.CustomFieldChanged]: 'Custom field changed',
};

export const ActionType = {
  AssignTo: 100000000, ChangeStatus: 100000001, SetDueDate: 100000002, AddLabel: 100000003,
  NotifyUser: 100000004, PostToChannel: 100000005, CreateFollowupTask: 100000006, SetPriority: 100000007,
  MoveToPhase: 100000008, MoveToSprint: 100000009, SendEmail: 100000010,
} as const;
export const ACTION_LABEL: Record<number, string> = {
  [ActionType.AssignTo]: 'Assign to',
  [ActionType.ChangeStatus]: 'Change status',
  [ActionType.SetDueDate]: 'Set due date',
  [ActionType.AddLabel]: 'Add label',
  [ActionType.NotifyUser]: 'Notify user',
  [ActionType.PostToChannel]: 'Post to channel',
  [ActionType.CreateFollowupTask]: 'Create follow-up task',
  [ActionType.SetPriority]: 'Set priority',
  [ActionType.MoveToPhase]: 'Move to phase',
  [ActionType.MoveToSprint]: 'Move to sprint',
  [ActionType.SendEmail]: 'Send email',
};

export const RuleScope = { Project: 100000000 } as const;
export const RULE_LIMIT = 50;

export async function getProjectRules(pca: IPublicClientApplication, projectId: string): Promise<AutomationRule[]> {
  return dvList<AutomationRule>(pca, 'dagrun_pm_automationrules', {
    select: FIELDS,
    filter: `_dagrun_pm_projectid_value eq ${projectId}`,
    orderby: 'createdon desc',
  });
}

export async function toggleRule(pca: IPublicClientApplication, id: string, enabled: boolean): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_automationrules', id, { dagrun_pm_isenabled: enabled });
}

export async function deleteRule(pca: IPublicClientApplication, id: string): Promise<void> {
  return dvDelete(pca, 'dagrun_pm_automationrules', id);
}

export interface RulePayload {
  name: string;
  triggerType: number;
  triggerParams: Record<string, unknown>;
  condition: { field: string; operator: string; value: string } | null;
  actionType: number;
  actionParams: Record<string, unknown>;
  isEnabled: boolean;
}

function toBody(p: RulePayload): Record<string, unknown> {
  return {
    dagrun_pm_name: p.name,
    dagrun_pm_triggertype: p.triggerType,
    dagrun_pm_triggerparamsjson: JSON.stringify(p.triggerParams ?? {}),
    dagrun_pm_conditionjson: p.condition ? JSON.stringify(p.condition) : null,
    dagrun_pm_actiontype: p.actionType,
    dagrun_pm_actionparamsjson: JSON.stringify(p.actionParams ?? {}),
    dagrun_pm_isenabled: p.isEnabled,
    dagrun_pm_scope: RuleScope.Project,
  };
}

export async function createRule(pca: IPublicClientApplication, projectId: string, p: RulePayload): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_automationrules', {
    ...toBody(p),
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${projectId})`,
  });
}

export async function updateRule(pca: IPublicClientApplication, id: string, p: RulePayload): Promise<void> {
  return dvUpdate(pca, 'dagrun_pm_automationrules', id, toBody(p));
}
